import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Article } from '@knust/api-interfaces';
import { debounceTime, filter, ReplaySubject, take } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ArticleService } from '../../utility/services/article.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  setSelectedArticle,
  unsetSelectedArticle,
} from '../../articles/+state/article.actions';
import { loadArticleStorageLocations } from '../../reusable-components/article-storage-locations/+state/article-storage-location.actions';
import { Actions, ofType } from '@ngrx/effects';
import { addItemsToTrunkSuccess } from '../+state/trunk.actions';
import { ScanArticleDialogComponent } from '../../articles/scan-article-dialog/scan-article-dialog.component';

@UntilDestroy()
@Component({
  selector: 'knust-add-article-to-trunk-dialog',
  templateUrl: './add-article-to-trunk-dialog.component.html',
  styleUrls: ['./add-article-to-trunk-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddArticleToTrunkDialogComponent implements OnInit {
  deliveryArticleForm: UntypedFormGroup = this.fb.group({
    article: this.fb.group({
      id: [null, Validators.required],
    }),
    storageLocationArticle: this.fb.control(null, Validators.required),
    amount: this.fb.control(0),
  });

  articles: Article[] = [];
  articleFilterCtrl: FormControl = new FormControl('');
  articleSearchTerm = '';
  public filteredArticles: ReplaySubject<Article[]> = new ReplaySubject<
    Article[]
  >(1);

  displayedColumns: string[] = [
    'storageLocation.label',
    'storageLocation.address.street',
    'actualStock',
    'reservedStock',
    'buyStockInTrunk',
  ];

  constructor(
    public dialogRef: MatDialogRef<AddArticleToTrunkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { trunkId: number | undefined },
    private dialog: MatDialog,
    private articleService: ArticleService,
    private fb: FormBuilder,
    private store: Store,
    private actions: Actions,
  ) {}

  ngOnInit(): void {
    this.filterArticles();

    this.articleFilterCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(500),
        filter((value) => value !== this.articleSearchTerm),
      )
      .subscribe(() => {
        this.filterArticles();
      });

    this.deliveryArticleForm
      .get('article.id')
      ?.valueChanges?.pipe(untilDestroyed(this))
      ?.subscribe((value) => {
        if (value) {
          this.store.dispatch(setSelectedArticle({ id: value }));
          setTimeout(
            () => this.store.dispatch(loadArticleStorageLocations()),
            250,
          );
        } else {
          this.store.dispatch(unsetSelectedArticle());
        }
      });

    this.actions
      .pipe(ofType(addItemsToTrunkSuccess), take(1))
      .subscribe(() => this.dialogRef.close());
  }

  filterArticles() {
    // Der Scan-Dialog liefert die Artikel-ID als Zahl
    this.articleSearchTerm = String(this.articleFilterCtrl.value ?? '');

    this.articleService
      .loadArticles(
        '?page=1&limit=10000&search=' + this.articleSearchTerm.toLowerCase(),
      )
      .subscribe((res) => this.filteredArticles.next(res.items));
  }

  scanArticle() {
    this.dialog
      .open(ScanArticleDialogComponent, {
        width: '80vw',
        maxWidth: 'min(90vw, 800px)',
      })
      .afterClosed()
      .subscribe((articleId) => {
        // Scan abgebrochen: aktuelle Auswahl und Suche beibehalten
        if (!articleId) {
          return;
        }

        this.articleFilterCtrl.setValue(articleId);

        this.deliveryArticleForm.get('article.id')?.setValue(articleId);
      });
  }
}
